import { Keyboard, View } from 'react-native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckoutLayout from '../CheckoutLayout';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input } from '~/components';
import CardBanner from './components/CardBanner';
import validate from 'card-validator';
import { isCpf } from '~/utils/yup';
import useAppNavigation from '~/hooks/useAppNavigation';
import SafeEnvironment from '../SafeEnvironment';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import { useCheckout, useSalesFunnel } from '~/stores';
import { CheckoutStepNames } from '~/utils/salesFunnel';

const schema = yup
  .object()
  .shape({
    cardNumber: yup.number().required('O número do cartão é obrigatório'),
    holderName: yup.string().required('O nome do titular é obrigatório'),
    document: isCpf(),
    expiryDate: yup
      .string()
      .required('A validade é obrigatória')
      .test({
        name: 'validateDate',
        message: 'Data de validade inválida',
        test: (value) => {
          if (value) {
            const [month, year] = value.split('/');

            if (!month || !year) {
              return false;
            }

            const currYear = Number(
              new Date().getFullYear().toString().substring(2)
            );

            if (Number(month) > 12 || Number(year) < currYear) {
              return false;
            }

            const currMonth = new Date().getMonth() + 1;

            if (Number(year) === currYear && Number(month) < currMonth) {
              return false;
            }

            return true;
          }

          return false;
        },
      }),
    CVV: yup
      .string()
      .min(3, 'O CVV informado não é válido')
      .max(4, 'O CVV informado não é válido')
      .required('O CVV é obrigatório.'),
  })
  .required();

interface CreditCardStep {
  cardNumber: number;
  holderName: string;
  document: string;
  expiryDate: string;
  CVV: number;
}

const CreditCard = () => {
  const { push } = useAppNavigation();
  const { setPage, setCreditCard } = useCheckout();
  const { newStepLog } = useSalesFunnel();

  const [cardType, setCardType] = useState<string>();
  const [cardSide, setCardSide] = useState<'front' | 'rear'>('front');

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_Checkout'>>();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<CreditCardStep>({
    resolver: yupResolver(schema),
  });

  const cardNumber = watch('cardNumber');
  const holderName = watch('holderName');
  const expiryDate = watch('expiryDate');

  const cvvLength = useMemo(() => {
    if (cardType && ['amex', 'american-express'].includes(cardType)) {
      return 4;
    }

    return 3;
  }, [cardType]);

  const onSubmit = useCallback(
    async (data: CreditCardStep) => {
      Keyboard.dismiss();
      setCreditCard(data);

      const nextStep = (params.step || 0) + 1;
      setPage(nextStep);
      push('MarisaServicos_Checkout', { step: nextStep });
    },
    [params.step, push, setCreditCard, setPage]
  );

  useEffect(() => {
    if (String(cardNumber).length > 15) {
      const card = validate.number(cardNumber);

      if (card.isPotentiallyValid && card.card?.type) {
        setCardType(card.card.type);
      }
    } else {
      setCardType(undefined);
    }
  }, [cardNumber]);

  useEffect(() => {
    newStepLog(CheckoutStepNames.CREDIT_CARD);
  }, [newStepLog]);

  return (
    <CheckoutLayout isValid onSubmit={handleSubmit(onSubmit)}>
      <SafeEnvironment />

      <CardBanner
        side={cardSide}
        cardType={cardType}
        cardNumber={cardNumber}
        holderName={holderName}
        expiryDate={expiryDate}
      />

      <Input
        name="cardNumber"
        control={control}
        label="Número do cartão"
        placeholder="Número do cartão"
        keyboardType="number-pad"
        maskConfig={{ type: 'credit-card' }}
        maxLength={19}
        error={errors.cardNumber?.message}
      />

      <Input
        name="holderName"
        control={control}
        label="Nome do titular do cartão"
        keyboardType="ascii-capable"
        placeholder="Nome do titular"
        error={errors.holderName?.message}
        maxLength={100}
      />

      <Input
        name="document"
        control={control}
        label="CPF"
        placeholder="CPF"
        keyboardType="number-pad"
        maskConfig={{ type: 'cpf' }}
        error={errors.document?.message}
        maxLength={14}
      />

      <View className="flex-row items-start">
        <Input
          name="expiryDate"
          control={control}
          label="Validade"
          placeholder="Mês/Ano"
          className="flex-1"
          keyboardType="number-pad"
          maskConfig={{
            type: 'custom',
            mask: '99/99',
          }}
          maxLength={5}
          error={errors.expiryDate?.message}
        />

        <Input
          name="CVV"
          control={control}
          label="Cód. segurança"
          placeholder="CVV"
          className="flex-1 ml-2"
          maxLength={cvvLength}
          keyboardType="number-pad"
          maskConfig={{ type: 'only-numbers' }}
          onFocus={() => setCardSide('rear')}
          onBlur={() => setCardSide('front')}
          error={errors.CVV?.message}
        />
      </View>
    </CheckoutLayout>
  );
};

export default CreditCard;
